import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { errorMessage } from "./fieldUtils";

const formData = (v) => ({ work_performed: v.work_performed || "", observations: v.observations || "", internal_notes: v.internal_notes || "", findings: (v.findings || []).map(({ id, machine, description, priority, recommendation, photo_ids }) => ({ id, machine, description, priority, recommendation, photo_ids })) });

export default function useFieldVisit(id) {
  const { user } = useAuth(); const key = `field-draft-${user.id}-${id}`;
  const [visit, setVisit] = useState(null); const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true); const [busy, setBusy] = useState(false);
  const [error, setError] = useState(""); const [draftNotice, setDraftNotice] = useState("");
  const [staleDraft, setStaleDraft] = useState(null); const actionLock = useRef(false);
  const accept = useCallback((data) => { setVisit(data); setDirty(false); setStaleDraft(null); setDraftNotice(""); try { localStorage.removeItem(key); } catch {} return data; }, [key]);
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/field/visits/${id}`); setVisit(data); setDirty(false);
      try {
        const draft = JSON.parse(localStorage.getItem(key) || "null");
        if (draft && ["scheduled", "in_progress"].includes(data.status)) {
          if (draft.version === data.version) { setVisit({ ...data, ...draft.form }); setDirty(true); setDraftNotice("Borrador local recuperado; aún no guardado en la plataforma."); }
          else { setStaleDraft(draft); setDraftNotice("La visita cambió desde tu borrador local. Revisa los datos antes de recuperarlo."); }
        }
      } catch { setDraftNotice("No se pudo recuperar el borrador local."); }
    } catch (err) { setError(errorMessage(err)); } finally { setLoading(false); }
  }, [id, key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!dirty || !visit) return;
    const timeout = setTimeout(() => {
      try { localStorage.setItem(key, JSON.stringify({ version: visit.version, form: formData(visit) })); }
      catch { setDraftNotice("El borrador local no pudo guardarse. Guarda en la plataforma antes de salir."); }
    }, 200);
    return () => clearTimeout(timeout);
  }, [dirty, visit, key]);
  useEffect(() => {
    const protect = (e) => { if (dirty) { try { localStorage.setItem(key, JSON.stringify({ version: visit.version, form: formData(visit) })); } catch {} e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", protect); return () => window.removeEventListener("beforeunload", protect);
  }, [dirty, key, visit]);
  const patch = (partial) => {
    setVisit((old) => ({ ...old, ...partial }));
    if (Object.keys(partial).some((k) => ["work_performed", "observations", "internal_notes", "findings"].includes(k))) setDirty(true);
  };
  const refreshMeta = async () => {
    const { data } = await api.get(`/field/visits/${id}`);
    if (dirty && data.version !== visit.version) setDraftNotice("Administración modificó la visita. Recarga para comparar antes de guardar.");
    setVisit((old) => ({ ...data, ...(dirty ? { ...formData(old), version: old.version } : {}) }));
  };
  const saveOrAct = async (action) => {
    if (actionLock.current) return;
    actionLock.current = true; setBusy(true);
    try {
      let current = visit;
      if (dirty || !action) { const { data } = await api.patch(`/field/visits/${id}/draft`, { version: visit.version, ...formData(visit) }); current = accept(data); }
      if (action) { const { data } = await api.post(`/field/visits/${id}/${action}`, { version: current.version }); accept(data); }
      toast.success(action === "submit" ? "Visita entregada al equipo" : action === "start" ? "Visita iniciada" : "Borrador guardado");
    } catch (err) { toast.error(errorMessage(err)); if (err.response?.status === 409) setDraftNotice("La visita cambió. Tu borrador se conserva: recarga para comparar."); }
    finally { actionLock.current = false; setBusy(false); }
  };
  return { visit, patch, loading, busy, error, dirty, load, refreshMeta, saveOrAct, draftNotice,
    staleDraft, recover: () => { patch(staleDraft.form); setStaleDraft(null); setDraftNotice("Borrador recuperado. Revísalo antes de guardar."); },
    discard: () => { try { localStorage.removeItem(key); } catch {} setStaleDraft(null); setDraftNotice(""); } };
}